/* The cart drawer, on the standard product template.
 *
 * What this does not do: add things to the cart. Horizon's product form does the add
 * and announces it with shopify:cart:lines-update; this listens for that, reads the
 * cart back, and puts itself on screen. Reimplementing the add would mean owning
 * variant resolution, selling plans, line item properties and error states, all of
 * which the theme already handles.
 *
 * The event is dispatched optimistically, before the request it describes has been
 * sent, so reading the cart when it arrives reads the cart as it was. It carries a
 * promise that settles when the mutation lands, and that is what this waits on. This
 * cost an hour: the first version read straight away, found nothing changed, and the
 * panel never opened while the add itself worked perfectly.
 *
 * What it carries, measured on the storefront rather than taken from the typings:
 *
 *   action        'add' | 'remove' | 'update'
 *   lines         [{ quantity, merchandiseId: 'gid://shopify/ProductVariant/123' }]
 *   promise       settles when the mutation has been applied
 *
 * All of it on the event object itself. event.detail is an empty object, which is
 * what the first version went looking in.
 */
(function () {
  var root = document.querySelector('.aps-drawer');
  if (!root) return;

  var panel = root.querySelector('[data-drw]');
  var tpl = root.querySelector('[data-drw-row]');
  var lineHost = root.querySelector('[data-drw-line]');
  var wrapper = document.querySelector('.page-wrapper');
  if (!panel || !tpl || !lineHost || !wrapper) return;

  var cfg = {};
  try { cfg = JSON.parse(root.querySelector('[data-drw-config]').textContent); } catch (e) { return; }

  var head = root.querySelector('.drw-hd');
  var away = root.querySelector('[data-drw-away]');
  var met = root.querySelector('[data-drw-met]');
  var awayAmt = root.querySelector('[data-drw-away-amt]');
  var bar = root.querySelector('[data-drw-bar]');
  var subEl = root.querySelector('[data-drw-sub]');

  var prev = null;        /* the cart as it was before the add being reported */
  var openKey = null;     /* the line the panel is currently showing */
  var busy = false;

  /* ---- money ---------------------------------------------------------------
     Two shapes, because the design uses two. The subtotal and the line price set
     the cents small and raised, which is markup; the shipping bar states a plain
     amount inside a sentence, which is text. */
  function pad(n) { return n < 10 ? '0' + n : '' + n; }
  function raised(cents) {
    /* Thousands separator and proportional digits, as aps-money-raised prints the same price (2026-09-19). */
    return '<span class="aps-pn">' + cfg.symbol + Math.floor(cents / 100).toLocaleString('en-US') + '<sup>' + pad(cents % 100) + '</sup></span>';
  }
  function plain(cents) {
    return cfg.symbol + (cents / 100).toFixed(2);
  }

  /* ---- the cart ------------------------------------------------------------ */
  function readCart() {
    return fetch(cfg.cartUrl + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) { return r.ok ? r.json() : null; }).catch(function () { return null; });
  }

  /* A map of line key to quantity. The key is Shopify's own, which is variant id
     plus a hash of the line's properties, so two lines of the same variant with
     different engraving stay two lines here as well. */
  function tally(cart) {
    var m = {};
    (cart && cart.items ? cart.items : []).forEach(function (it) { m[it.key] = it.quantity; });
    return m;
  }

  /* The line the event was about. The variant names it, which is exact where a
     difference is a guess; the difference is only used to break a tie, for the case
     where one variant sits on two lines because they carry different properties. */
  function lineFor(cart, variantId, before) {
    var mine = (cart.items || []).filter(function (it) { return it.variant_id === variantId; });
    if (!mine.length) return null;
    var grew = mine.filter(function (it) {
      var was = before && Object.prototype.hasOwnProperty.call(before, it.key) ? before[it.key] : 0;
      return it.quantity > was;
    });
    return (grew.length ? grew : mine)[grew.length ? grew.length - 1 : mine.length - 1];
  }

  /* gid://shopify/ProductVariant/123 is what the event carries; /cart.js speaks in
     bare numbers. */
  function variantOf(line) {
    var id = line && line.merchandiseId;
    if (typeof id === 'number') return id;
    var m = String(id || '').match(/(\d+)\s*$/);
    return m ? Number(m[1]) : null;
  }

  /* ---- painting ------------------------------------------------------------ */
  /* The header badge, which nothing else updates. sections/aps-header.liquid prints
     cart.item_count once and leaves it, which was true enough while every add
     reloaded the page or opened a drawer that was about to be navigated away from.
     An add that changes nothing but this panel leaves the badge reading whatever it
     read when the page was built, so the panel keeps it honest. The attribute is
     already on the span; this is the first thing to use it. */
  function paintCount(cart) {
    /* The panel's own chip is written from the same place as the header's, since
       they are the same count wearing the same chip. */
    document.querySelectorAll('[data-aps-cart-count], [data-drw-count]').forEach(function (el) {
      el.textContent = cart.item_count;
    });
  }

  function paintTotals(cart) {
    paintCount(cart);
    if (subEl) subEl.innerHTML = raised(cart.items_subtotal_price);

    if (!cfg.threshold || cfg.threshold <= 0) return;
    var short = cfg.threshold - cart.items_subtotal_price;
    var isMet = short <= 0;
    if (away) away.hidden = isMet;
    if (met) met.hidden = !isMet;
    if (!isMet) {
      if (awayAmt) awayAmt.textContent = plain(short);
      if (bar) {
        var pct = Math.round((cart.items_subtotal_price / cfg.threshold) * 100);
        bar.style.width = Math.max(0, Math.min(100, pct)) + '%';
      }
    }
  }

  function paintLine(item) {
    lineHost.textContent = '';
    var row = tpl.content.cloneNode(true);
    var img = row.querySelector('img');
    if (item.image) {
      /* Shopify's own resizing, asked for at twice the drawn size so the thumbnail
         is not soft on a retina screen. */
      img.src = item.image.replace(/(\.[a-z]+)(\?|$)/i, '_128x$1$2');
      img.alt = '';
    } else {
      img.remove();
    }
    var a = row.querySelector('.drw-ti');
    a.textContent = item.product_title;
    a.href = item.url;

    var opt = row.querySelector('.drw-opt');
    /* Shopify gives a variant with no options the title "Default Title". Printing it
       tells the shopper the developer's word for nothing. */
    var name = item.variant_title && item.variant_title !== 'Default Title' ? item.variant_title : '';
    if (name) { opt.textContent = name; } else { opt.remove(); }

    /* Unadvertised Deal label, for a line whose variant has it. The line comes from
       /cart.js, which has no metafields, so the product page's variant data decides. */
    var deal = row.querySelector('[data-drw-deal]');
    if (deal) deal.hidden = !(window.APSDeal && window.APSDeal.isEligible(item.variant_id));

    row.querySelector('[data-drw-qty]').textContent = item.quantity;
    row.querySelector('[data-drw-minus]').disabled = item.quantity <= 1;
    row.querySelector('.drw-pz').innerHTML = raised(item.final_line_price);

    lineHost.appendChild(row);
    openKey = item.key;
  }

  /* ---- the arrival --------------------------------------------------------
     Started here rather than by the rule, because the line is painted while the
     panel is still parked off the right edge. An animation that begins there has
     run its course by the time the slide brings the panel into view, which is
     exactly what happened: every class was set, every animation-name computed,
     and nothing was ever seen.

     So it waits for the slide. Only when the panel was closed: adding into a
     panel that is already open has no slide to wait for, and half a second of
     nothing is worse than no animation at all.

     The class comes off and goes back on with a reflow between. Without the
     reflow the two changes are coalesced before the next frame and the browser
     sees no change, so the second add would not replay. */
  function pop(hadToSlide) {
    var wait = 0;
    if (hadToSlide) {
      var d = parseFloat(getComputedStyle(panel).transitionDuration) || 0.3;
      wait = d * 1000;
    }
    setTimeout(function () {
      [lineHost.querySelector('.drw-row'), head].forEach(function (n) {
        if (!n) return;
        n.classList.remove('is-pop');
        void n.offsetWidth;
        n.classList.add('is-pop');
      });
    }, wait);
  }

  /* ---- open and close ------------------------------------------------------ */
  function setOpen(on) {
    panel.classList.toggle('is-open', on);
    panel.setAttribute('aria-hidden', on ? 'false' : 'true');
    wrapper.classList.toggle('aps-push-open', on);
  }
  function close() { setOpen(false); openKey = null; }

  root.querySelectorAll('[data-drw-close]').forEach(function (b) {
    b.addEventListener('click', close);
  });
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && panel.classList.contains('is-open')) close();
  });

  /* ---- changing the line ---------------------------------------------------
     Removing the line closes the panel. The panel is the answer to "did that go
     in", and taking it back out again unasks the question; leaving an empty panel
     with a rail in it would be a shop window where a receipt was. */
  function change(qty) {
    if (busy || !openKey) return;
    busy = true;
    fetch(cfg.cartUrl + '/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: openKey, quantity: qty })
    }).then(function (r) { return r.json(); }).then(function (cart) {
      prev = tally(cart);
      announce(cart);
      /* The badge before the branch, not after it. Removing the line takes the early
         exit below, and with the repaint on the far side of that the header went on
         claiming an item that was no longer there. */
      paintCount(cart);
      if (qty <= 0) { close(); return; }
      var still = (cart.items || []).filter(function (it) { return it.key === openKey; })[0];
      if (still) { paintLine(still); } else { close(); return; }
      paintTotals(cart);
    }).catch(function () {}).then(function () { busy = false; });
  }

  lineHost.addEventListener('click', function (e) {
    var el = e.target.closest ? e.target.closest('button') : null;
    if (!el) return;
    var qty = parseInt(lineHost.querySelector('[data-drw-qty]').textContent, 10) || 1;
    if (el.hasAttribute('data-drw-plus')) change(qty + 1);
    else if (el.hasAttribute('data-drw-minus')) change(qty - 1);
    else if (el.hasAttribute('data-drw-remove')) change(0);
  });

  /* ---- the rail ------------------------------------------------------------
     Adding from here does not repaint the panel around the new item. The shopper
     is reading this list; moving it under them loses their place. The button says
     what happened where they are looking, and the totals above it keep up. */
  root.querySelectorAll('[data-drw-add]').forEach(function (b) {
    b.addEventListener('click', function () {
      if (busy || b.classList.contains('is-added')) return;
      busy = true;
      fetch(cfg.cartUrl + '/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: Number(b.getAttribute('data-id')), quantity: 1 }] })
      }).then(function (r) {
        if (!r.ok) throw new Error('add failed');
        b.classList.add('is-added');
        return readCart();
      }).then(function (cart) {
        if (!cart) return;
        prev = tally(cart);
        paintTotals(cart);
        announce(cart);
      }).catch(function () {}).then(function () { busy = false; });
    });
  });

  /* Tell the rest of the theme what this panel did, so the header count keeps up.
     The shape matters: assets/cart-icon.js reads event.promise and takes the count
     from what it resolves with, and an event without one is silently ignored. That
     is why the first version left the bubble reading zero while the cart filled up.
     The properties go on the event object rather than in detail, which is where
     Horizon puts its own. Announced as an update, never an add, so the listener at
     the bottom of this file does not treat the panel's own work as a reason to
     reopen. */
  function announce(cart) {
    var e = new CustomEvent('shopify:cart:lines-update', { bubbles: true });
    e.action = 'update';
    e.source = 'aps-drawer';
    e.promise = Promise.resolve({ cart: { totalQuantity: cart.item_count } });
    document.dispatchEvent(e);
  }

  /* ---- the trigger --------------------------------------------------------- */
  document.addEventListener('shopify:cart:lines-update', function (e) {
    /* Only an add opens it. A quantity change or a removal is somebody adjusting
       what they already decided on, and the panel has nothing to tell them about
       that. This also passes over the panel's own work, which announces itself as
       an update. */
    if (e.action !== 'add') return;

    var line = e.lines && e.lines.length ? e.lines[0] : null;
    var variantId = variantOf(line);
    var before = prev;

    /* Wait for the mutation, then read the cart. The promise is the theme's own
       signal that the add has been applied; the timeout is there so a promise that
       never settles cannot leave the panel permanently closed. */
    var settled = e.promise && typeof e.promise.then === 'function'
      ? Promise.race([e.promise, new Promise(function (r) { setTimeout(r, 4000); })])
      : new Promise(function (r) { setTimeout(r, 700); });

    settled.catch(function () {}).then(readCart).then(function (cart) {
      if (!cart) return;
      var item = variantId ? lineFor(cart, variantId, before) : null;
      prev = tally(cart);
      if (!item) return;
      var wasOpen = panel.classList.contains('is-open');
      paintLine(item);
      paintTotals(cart);
      setOpen(true);
      pop(!wasOpen);
    });
  });

  /* Seed the comparison, so the first add on the page is measured against a cart
     that is actually known rather than against nothing. */
  readCart().then(function (cart) { if (cart) { prev = tally(cart); paintTotals(cart); } });
}());
